import { NbMenuItem } from '@nebular/theme';
import { DefinicoesFormulario } from './extra-components/form-generico/form-generico.component';

/** Mapeamento do título do grupo do menu para a categoria do assistente virtual */
const GRUPO_PARA_CATEGORIA: { [key: string]: string } = {
  'RenoVerse AI': 'ai',
  'Suporte': 'utilidades'
};

const CATEGORIA_PADRAO = 'principal';

function menuItemParaCategoria(parentTitle: string | null): string {
  if (!parentTitle) return CATEGORIA_PADRAO;
  return GRUPO_PARA_CATEGORIA[parentTitle] ?? CATEGORIA_PADRAO;
}

/**
 * Retorna um mapa de link -> { title, icon, categoria } para todos os itens
 * do menu que possuem link (incluindo filhos). Usado pelo assistente virtual
 * para montar atalhos a partir da API sem duplicar dados do menu.
 */
export function getMenuMapByLink(): Map<string, { title: string; icon: string; categoria: string }> {
  const map = new Map<string, { title: string; icon: string; categoria: string }>();

  function visit(items: NbMenuItem[], parentTitle: string | null): void {
    for (const item of items) {
      if (item.link) {
        const icon = item.icon && typeof item.icon === 'string' ? item.icon : 'file-outline';
        map.set(item.link, {
          title: item.title || '',
          icon,
          categoria: menuItemParaCategoria(parentTitle)
        });
      }
      if (item.children && item.children.length) {
        visit(item.children, item.title || parentTitle);
      }
    }
  }

  visit(MENU_ITEMS, null);
  return map;
}

/**
 * Retorna um mapa de title -> { link, icon, categoria } para todos os itens
 * do menu que possuem link. Usado pelo assistente virtual quando a API
 * retorna atalhos por SubMenuDescricao (título) em vez de link.
 */
export function getMenuMapByTitle(): Map<string, { link: string; icon: string; categoria: string }> {
  const map = new Map<string, { link: string; icon: string; categoria: string }>();

  function visit(items: NbMenuItem[], parentTitle: string | null): void {
    for (const item of items) {
      if (item.link && item.title) {
        const icon = item.icon && typeof item.icon === 'string' ? item.icon : 'file-outline';
        map.set(item.title, {
          link: item.link,
          icon,
          categoria: menuItemParaCategoria(parentTitle)
        });
      }
      if (item.children && item.children.length) {
        visit(item.children, item.title || parentTitle);
      }
    }
  }

  visit(MENU_ITEMS, null);
  return map;
}

export const MENU_ITEMS: NbMenuItem[] = [
  {
    title: 'Painel de Negócio',
    icon: 'pie-chart',
    link: '/pages/negocio/painel-vendas'
  },
  {
    title: 'Painel de Negócio (Gerente)',
    icon: 'pie-chart',
    link: '/pages/negocio/painel-vendas-gerente'
  },
  {
    title: 'Painel Operacional',
    icon: 'clipboard-outline',
    link: '/pages/painel-operacional/painel'
  },
  {
    title: 'Solicitações de Documento',
    icon: 'file-text-outline',
    link: '/pages/cadastro/solicitacoes-documento'
  },
  {
    title: 'Admin',
    icon: 'settings-outline',
    children: [
      {
        title: 'Visualizar Log',
        icon: 'compass-outline',
        link: '/pages/admin/visualizar-log'
      },
      {
        title: 'Usuário Online',
        icon: 'person',
        link: '/pages/admin/usuario-online'
      },
      {
        title: 'Ativos Bolsa',
        icon: 'trending-up-outline',
        link: '/pages/admin/ativos-bolsa'
      },
      {
        title: 'Permissões de Acesso',
        icon: 'lock-outline',
        link: '/pages/admin/permissao-acesso'
      },
      {
        title: 'Aniversários',
        icon: 'award-outline',
        link: '/pages/admin/aniversarios'
      },
      {
        title: 'Criar Notificação',
        icon: 'bell-outline',
        link: '/pages/admin/notificacao-admin'
      },
      {
        title: 'Notificação de Manutenção',
        icon: 'alert-triangle-outline',
        link: '/pages/admin/notificacao-manutencao'
      },
      {
        title: 'CPF Receita',
        icon: 'file-text-outline',
        link: '/pages/admin/processamento-cpf'
      },
      {
        title: 'Fechamentos',
        icon: 'trending-up-outline',
        link: '/pages/admin/rendimentos'
      },
      {
        title: 'Acomp. de Processo',
        icon: 'file-text-outline',
        link: '/pages/admin/acompanhamento-processo'
      }
    ]
  },
  {
    title: 'Configuração',
    icon: 'file-text-outline',
    children: [
      {
        title: 'Situação do CPF/CNPj',
        icon: 'file-add-outline',
        link: `/pages/configuracao/form/${DefinicoesFormulario.SITUACAO_CPF_CNPJ}`
      },
      {
        title: 'Situação do Negócio',
        icon: 'file-add-outline',
        link: `/pages/configuracao/form/${DefinicoesFormulario.SITUACAO_NEGOCIO}`
      },
      {
        title: 'Cadastro',
        icon: 'file-add-outline',
        link: `/pages/configuracao/form/${DefinicoesFormulario.CADASTRO}`
      },
      {
        title: 'Probabilidade',
        icon: 'file-add-outline',
        link: `/pages/configuracao/form/${DefinicoesFormulario.PROBABILIDADE}`
      },
      {
        title: 'Trabalho Completo' ,
        icon: 'file-add-outline',
        link: `/pages/configuracao/form/${DefinicoesFormulario.TRABALHO_COMPLETO}`
      },
      {
        title: 'Andamento do Processo',
        icon: 'file-add-outline',
        link: `/pages/configuracao/form/${DefinicoesFormulario.ANDAMENTO_PROCESSO}`
      },
      {
        title: 'Classificação',
        icon: 'file-add-outline',
        link: `/pages/configuracao/form/${DefinicoesFormulario.CLASSIFICACAO}`
      },
      {
        title: 'Tipo de Comissões',
        icon: 'file-add-outline',
        link: `/pages/configuracao/form/${DefinicoesFormulario.TIPO_COMISSOES}`
      },
      {
        title: 'Tipo Comissões Receb.',
        icon: 'file-add-outline',
        link: `/pages/configuracao/form/${DefinicoesFormulario.TIPO_COMISSOES_RECEB}`
      },
      {
        title: 'Tipo de Gasto',
        icon: 'file-add-outline',
        link: `/pages/configuracao/form/${DefinicoesFormulario.TIPO_GASTO}`
      },
      {
        title: 'Inventário',
        icon: 'file-add-outline',
        link: `/pages/configuracao/form/${DefinicoesFormulario.INVENTARIO}`
      },
      {
        title: 'Conhecimento de Ativos',
        icon: 'file-add-outline',
        link: `/pages/configuracao/form/${DefinicoesFormulario.CONHECIMENTO_ATIVOS}`
      },
      {
        title: 'Fase do Negócio',
        icon: 'file-add-outline',
        link: `/pages/configuracao/form/${DefinicoesFormulario.FASE_NEGOCIO}`
      },
      {
        title: 'Provedores de LLM',
        icon: 'bulb-outline',
        link: '/pages/configuracao/select-llm-providers'
      },
      {
        title: 'Status Transfêrencia',
        icon: 'file-add-outline',
        link: `/pages/configuracao/form/${DefinicoesFormulario.STATUS_TRANSFERENCIA}`
      },
      {
        title: 'Tempo Limite Solicitação Doc.',
        icon: 'file-add-outline',
        link: '/pages/configuracao/tempo-limite-solicitacao-documento'
      }
    ]
  },
  {
    title: 'Cadastro',
    icon: 'file-text-outline',
    children: [
      {
        title: 'Usuários',
        icon: 'person-add-outline',
        link: '/pages/cadastro/usuarios'
      },
      {
        title: 'Importar Arquivo',
        icon: 'cloud-upload-outline',
        link: '/pages/cadastro/importar-arquivo'
      },
      {
        title: 'Atualizar CPF',
        icon: 'cloud-download-outline',
        link: '/pages/cadastro/atualizar-cpf'
      },
      {
        title: 'Time Executivo',
        icon: 'people-outline',
        link: '/pages/cadastro/cadastro-time-executivos'
      }
    ]
  },
  {
    title: 'Negócio',
    icon: 'briefcase',
    children: [
      {
        title: 'Cadastro Cliente',
        icon: 'person-add-outline',
        link: '/pages/negocio/cadastro-cliente'
      },
      {
        title: 'Cliente',
        icon: 'person',
        link: '/pages/negocio/cliente'
      },
      {
        title: 'Relatório',
        icon: 'file-text-outline',
        link: '/pages/negocio/relatorio'
      },

    ]
  },
  {
    title: 'Processos Jurídicos',
    icon: 'file-text-outline',
    link: '/pages/negocio/processos-juridicos'
  },
  {
    title: 'Suporte',
    icon: 'headphones-outline',
    children: [
      {
        title: 'Reclamação/Sugestão',
        icon: 'message-square-outline',
        link: '/pages/suporte/reclamacao-sugestao'
      },
      {
        title: 'FAQ (Perguntas Frequentes)',
        icon: 'question-mark-circle-outline',
        link: '/pages/suporte/faq'
      }
    ]
  },
  {
    title: 'RenoVerse AI',
    icon: 'message-circle-outline',
    children: [
      {
        title: 'Chat com IA',
        icon: 'message-square-outline',
        link: '/pages/configuracao/renoverse-ai/chat-ia'
      },
      {
        title: 'Chat com PDF',
        icon: 'file-text-outline',
        link: '/pages/configuracao/renoverse-ai/chat-pdf'
      }
    ]
  },
  {
    title: 'Financeiro',
    icon: 'credit-card-outline',
    children: [
      {
        title: 'Previsão de Caixa',
        icon: 'trending-up-outline',
        link: '/pages/financeiro/previsao-caixa'
      },
      {
        title: 'Importar Gastos',
        icon: 'cloud-upload-outline',
        link: '/pages/financeiro/importar-gastos'
      }
    ]
  }
];
